/*
Copyright (c) 2011, Rob Ellis, Chris Umbel

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/

/*
Leaving the license agreement in as large parts have been borrowed from the Natural tf-idf lib (MIT licensed)
*/

import XRegExp from "xregexp";

// We use the XRegExp library as Unicode regex support seems to be very new (ES2018) with limited browser support
// By using this lib, we can use the new format of RegEx (\p{L}) as-used in "pattern" below making it
// simpler to switch later
const pattern = "[^\\p{L}0-9 ]";

// text should be an array
export const buildDocument = (text, key, stopwords) => {
    let re = new XRegExp(pattern, "gu");

    if (typeof text === "string") {
        text = text
            .toLowerCase()
            .replace(re, "")
            .trim()
            .split(" ");
    } else if (!Array.isArray(text)) {
        return text;
    }

    text = text.filter(term => {
        return term.length > 1 && !(term in stopwords);
    });

    return text.reduce(
        function(document, term) {
            if (typeof document[term] === "function") document[term] = 0;
            document[term] = document[term] ? document[term] + 1 : 1;
            return document;
        },
        { __key: key }
    );
};

export const tf = (term, document) => {
    return document[term] ? document[term] : 0;
};

export const documentHasTerm = (term, document) => {
    return document[term] && document[term] > 0;
};

function TfIdf(stopwords) {
    this.stopwords = stopwords ? stopwords : {};
    this.documents = [];
    this._idfCache = {};
}

TfIdf.prototype.idf = function(term, force) {
    // Lookup the term in the New term-IDF caching,
    // this will cut search times down exponentially on large document sets.
    if (
        this._idfCache[term] &&
        this._idfCache.hasOwnProperty(term) &&
        force !== true
    )
        return this._idfCache[term];

    const docsWithTerm = this.documents.reduce(function(count, document) {
        return count + (documentHasTerm(term, document) ? 1 : 0);
    }, 0);

    const idf = 1 + Math.log(this.documents.length / (1 + docsWithTerm));

    // Add the idf to the term cache and return it
    this._idfCache[term] = idf;
    return idf;
};

// If restoreCache is set to true, all terms idf scores currently cached will be recomputed.
// Otherwise, the cache will just be wiped clean
TfIdf.prototype.addDocument = function(document, key, restoreCache) {
    this.documents.push(buildDocument(document, key, this.stopwords));

    // make sure the cache is invalidated when new documents arrive
    if (restoreCache === true) {
        for (let term in this._idfCache) {
            // invoking idf with the force option set will
            // force a recomputation of the idf, and it will
            // automatically refresh the cache value.
            this.idf(term, true);
        }
    } else {
        this._idfCache = {};
    }
};

TfIdf.prototype.tfidf = function(terms, d) {
    const _this = this;

    if (!Array.isArray(terms)) {
        let re = new XRegExp(pattern, "g");
        //terms = tokenizer.tokenize(terms.toString().toLowerCase());
        terms = terms
            .toLowerCase()
            .replace(re, "")
            .trim()
            .split(" ");
    }

    return terms.reduce(function(value, term) {
        let idf = _this.idf(term);
        idf = idf === Infinity ? 0 : idf;
        return value + tf(term, _this.documents[d]) * idf;
    }, 0.0);
};

TfIdf.prototype.listTerms = function(d) {
    const terms = [];

    for (let term in this.documents[d]) {
        if (term !== "__key")
            terms.push({ term: term, tfidf: this.tfidf(term, d) });
    }

    return terms.sort(function(x, y) {
        return y.tfidf - x.tfidf;
    });
};

TfIdf.prototype.tfidfs = function(terms, callback) {
    const tfidfs = new Array(this.documents.length);

    for (let i = 0; i < this.documents.length; i++) {
        tfidfs[i] = this.tfidf(terms, i);

        if (callback) callback(i, tfidfs[i], this.documents[i].__key);
    }

    return tfidfs;
};

export default TfIdf;
